const STORAGE_KEY = 'asi:history-metadata';

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readStorage() {
  if (!isBrowser()) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
    return {};
  } catch (error) {
    return {};
  }
}

function writeStorage(metadata) {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(metadata || {}));
  } catch (error) {
    // Swallow storage errors silently to avoid interrupting the UI flow.
  }
}

function normalizeTagLabel(label) {
  return String(label || '')
    .split(/\s+/)
    .map((word) => {
      if (!word) {
        return '';
      }
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ')
    .trim();
}

function sanitizeTagArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set();
  const normalized = [];
  value.forEach((tag) => {
    const label = normalizeTagLabel(tag);
    if (!label) {
      return;
    }
    const key = label.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push(label);
  });
  return normalized;
}

function getStoredTagMap() {
  const metadata = readStorage();
  const tags = metadata && metadata.tags && typeof metadata.tags === 'object' ? metadata.tags : {};
  return Object.keys(tags).reduce((accumulator, id) => {
    const normalized = sanitizeTagArray(tags[id]);
    if (normalized.length > 0) {
      accumulator[id] = normalized;
    }
    return accumulator;
  }, {});
}

function writeTagMap(nextMap) {
  const metadata = readStorage();
  writeStorage({
    ...metadata,
    tags: nextMap,
  });
}

export function replaceHistoryTagMap(nextMap = {}) {
  if (typeof nextMap !== 'object' || nextMap === null) {
    writeTagMap({});
    return {};
  }
  const sanitized = {};
  Object.keys(nextMap).forEach((id) => {
    const normalized = sanitizeTagArray(nextMap[id]);
    if (normalized.length) {
      sanitized[id] = normalized;
    }
  });
  writeTagMap(sanitized);
  return getHistoryTagMap();
}

export function getHistoryTitleMap() {
  const metadata = readStorage();
  if (!metadata || typeof metadata !== 'object') {
    return {};
  }

  const titles = metadata.titles;
  if (!titles || typeof titles !== 'object') {
    return {};
  }

  return Object.keys(titles).reduce((accumulator, id) => {
    const value = titles[id];
    if (typeof value === 'string' && value.trim()) {
      accumulator[id] = value;
    }
    return accumulator;
  }, {});
}

export function saveHistoryTitleMap(nextMap) {
  const metadata = readStorage();
  const normalizedMap =
    nextMap && typeof nextMap === 'object'
      ? Object.keys(nextMap).reduce((accumulator, id) => {
          const value = nextMap[id];
          if (typeof value === 'string' && value.trim()) {
            accumulator[id] = value.trim();
          }
          return accumulator;
        }, {})
      : {};

  writeStorage({
    ...metadata,
    titles: normalizedMap,
  });

  return normalizedMap;
}

export function setHistoryTitle(id, title) {
  if (!id) {
    return getHistoryTitleMap();
  }

  const trimmed = typeof title === 'string' ? title.trim() : '';
  const metadata = readStorage();
  const currentTitles =
    metadata && metadata.titles && typeof metadata.titles === 'object'
      ? { ...metadata.titles }
      : {};

  if (!trimmed) {
    delete currentTitles[id];
  } else {
    currentTitles[id] = trimmed;
  }

  writeStorage({
    ...metadata,
    titles: currentTitles,
  });

  return getHistoryTitleMap();
}

export function removeHistoryTitles(ids = []) {
  if (!Array.isArray(ids) || ids.length === 0) {
    return getHistoryTitleMap();
  }

  const metadata = readStorage();
  const currentTitles =
    metadata && metadata.titles && typeof metadata.titles === 'object'
      ? { ...metadata.titles }
      : {};

  let changed = false;
  ids.forEach((id) => {
    if (id && currentTitles[id]) {
      delete currentTitles[id];
      changed = true;
    }
  });

  if (changed) {
    writeStorage({
      ...metadata,
      titles: currentTitles,
    });
  }

  return getHistoryTitleMap();
}

export function pruneHistoryTitles(validIds = []) {
  const metadata = readStorage();
  const currentTitles =
    metadata && metadata.titles && typeof metadata.titles === 'object'
      ? { ...metadata.titles }
      : {};

  if (!currentTitles || Object.keys(currentTitles).length === 0) {
    return {};
  }

  const idSet = new Set(
    Array.isArray(validIds) ? validIds.filter((id) => typeof id === 'string' && id) : [],
  );

  let changed = false;
  Object.keys(currentTitles).forEach((id) => {
    if (!idSet.has(id)) {
      delete currentTitles[id];
      changed = true;
    }
  });

  if (changed) {
    writeStorage({
      ...metadata,
      titles: currentTitles,
    });
  }

  return getHistoryTitleMap();
}

export function getHistoryTagMap() {
  return getStoredTagMap();
}

export function setHistoryEntryTags(id, tags = []) {
  if (!id) {
    return getHistoryTagMap();
  }

  const normalizedTags = sanitizeTagArray(tags);
  const currentMap = getStoredTagMap();

  if (!normalizedTags.length) {
    if (currentMap[id]) {
      delete currentMap[id];
      writeTagMap(currentMap);
    }
    return getHistoryTagMap();
  }

  currentMap[id] = normalizedTags;
  writeTagMap(currentMap);
  return getHistoryTagMap();
}

export function removeHistoryTags(ids = []) {
  if (!Array.isArray(ids) || ids.length === 0) {
    return getHistoryTagMap();
  }

  const currentMap = getStoredTagMap();
  let changed = false;

  ids.forEach((id) => {
    if (id && currentMap[id]) {
      delete currentMap[id];
      changed = true;
    }
  });

  if (changed) {
    writeTagMap(currentMap);
  }

  return getHistoryTagMap();
}

export function pruneHistoryTags(validIds = []) {
  const currentMap = getStoredTagMap();

  if (!Object.keys(currentMap).length) {
    return {};
  }

  const idSet = new Set(
    Array.isArray(validIds) ? validIds.filter((id) => typeof id === 'string' && id) : [],
  );

  let changed = false;
  Object.keys(currentMap).forEach((id) => {
    if (!idSet.has(id)) {
      delete currentMap[id];
      changed = true;
    }
  });

  if (changed) {
    writeTagMap(currentMap);
  }

  return getHistoryTagMap();
}
